import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { useEffect, useMemo, useRef, useState } from "react";
import { BiCalculator, BiSolidMapPin } from "react-icons/bi";
import { GrDocumentPdf } from "react-icons/gr";
import { LiaFileContractSolid } from "react-icons/lia";
import { LuChevronDown, LuPlus, LuSparkles } from "react-icons/lu";
import { MdSave } from "react-icons/md";
import CommonPdfPreviewModal from "../Common/CommonPdfPreviewModal";
import PdfDataComponent from "./VesselPdf";

// Small "New X" dropdown: With AI / Manual.
function CreateMenu({ label, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  const item = "w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium text-slate-900 hover:bg-slate-100 text-left cursor-pointer border-none bg-transparent";
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="h-7 px-2.5 flex items-center gap-1.5 rounded-none bg-white border border-slate-300 text-slate-900 text-[11px] font-medium hover:bg-slate-100 shadow-sm transition-colors whitespace-nowrap"
      >
        <LuPlus size={12} />
        <span>{label}</span>
        <LuChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute top-8 left-0 z-[9999] w-40 bg-white border border-slate-300 rounded-none shadow-lg py-1">
          <button type="button" className={item} onClick={() => { setOpen(false); onSelect("ai"); }}>
            <LuSparkles size={13} /> With AI
          </button>
          <button type="button" className={item} onClick={() => { setOpen(false); onSelect("manual"); }}>
            <LuPlus size={13} /> Manual
          </button>
        </div>
      )}
    </div>
  );
}

function ActionButtons(props) {
  const {
    IsQuerySelected = true,
    isFromSingleVesselCalcy = false,
    hideOrder = false,
    setHideOrder = () => { },
    onClickCalculate = () => { },
    isExpanded = false,
    onClickCalculateAndSave = () => { },
    activeSection,
    onSetHideCalculator = () => { },
    onClickCopyToEstimate = () => { },
    isOperationPage = false,
    onSetOpenMap = () => { },
    isPublic = false,
    pdfdata,
    isFromCargoAnalysis = false,
    isFromMatrix = false,
    isContractPage,
    isFromSavedContracts = false,
    onClickCreateContract = null,
    onCreateCargo = null,
    onCreateVessel = null
  } = props;

  const sections = [
    { key: "calculator", label: "Calculations" },
    { key: "graph", label: "Graph" },
    { key: "portList", label: "Port List" },
    { key: "map", label: "Map" },
  ];
  const [isOpen, setIsOpen] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);
  const dropdownRef = useRef(null);

  const toggleSection = (sectionKey) => {
    onSetHideCalculator(sectionKey);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const count = useMemo(() => activeSection && sections.map(section => {
    const sectionKey = "hide" + section.key.charAt(0).toUpperCase() + section.key.slice(1);
    const isActive = activeSection[sectionKey] === false;
    return isActive;
  }).filter(Boolean).length, [activeSection]);

  // Common button class for consistency and compactness
  const btnClass = "h-7 px-2.5 min-w-fit cal-buttons-color border border-neutrals6 rounded-none text-[11px] font-medium flex items-center justify-center gap-1.5 whitespace-nowrap transition-all hover:opacity-90 shrink-0";
  const iconSize = 14;

  const handleOpenPdfModal = () => setPdfModal(true);
  const handleClosePdfModal = () => setPdfModal(false);

  return (
    <div className="action-background actions-buttons-div p-1.5 w-full overflow-visible">
      <div className="action-buttons-toolbar flex justify-between items-center w-full flex-nowrap gap-2 overflow-visible">

        {/* --- LEFT SIDE: Matrix/Section Controls --- */}
        <div className="action-buttons-primary flex items-center gap-2 shrink-0">
          {isFromMatrix && (
            <>
              <button
                onClick={() => setHideOrder((prev) => !prev)}
                data-spotlight="matrix-cargo-toggle"
                className="h-7 px-2.5 flex items-center gap-1.5 rounded-none bg-[#313C4C] text-white text-[11px] font-medium hover:bg-[#252e3b] shadow-sm transition-all duration-200 whitespace-nowrap shrink-0"
              >
                <SwapHorizIcon style={{ fontSize: 15 }} className="text-white" />
                <span>{hideOrder ? 'Show Cargo List' : 'Hide Cargo List'}</span>
              </button>

              {onCreateCargo && <CreateMenu label="New Cargo" onSelect={onCreateCargo} />}
              {onCreateVessel && <CreateMenu label="New Vessel" onSelect={onCreateVessel} />}

              {isExpanded && activeSection && (
                <div ref={dropdownRef} className="relative font-sans">
                  <button
                    onClick={() => setIsOpen(prev => !prev)}
                    className="h-7 px-2.5 flex items-center justify-between gap-1.5 bg-white border border-gray-300 rounded-none shadow-sm hover:shadow-md focus:outline-none text-gray-700 text-[11px] font-medium whitespace-nowrap shrink-0"
                  >
                    <span className={`truncate ${count > 0 ? "text-gray-900 font-medium" : "text-gray-500 font-medium"}`}>
                      {count > 0 ? `Sections (${count})` : "View Sections"}
                    </span>
                    <div className="flex items-center gap-1">
                      {count > 0 && <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>}
                      <svg className={`w-3 h-3 transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {isOpen && (
                    <div className="absolute top-8 left-0 z-[9999] w-48 bg-white border border-gray-300 rounded-none shadow-lg py-1">
                      {sections.map(section => {
                        const sectionKey = "hide" + section.key.charAt(0).toUpperCase() + section.key.slice(1);
                        const isActive = activeSection[sectionKey] === false;
                        return (
                          <label key={section.key} className="flex items-center px-3 py-1.5 cursor-pointer hover:bg-sky-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => toggleSection(sectionKey)}
                              className="mr-2 h-3 w-3 text-sky-600 border-gray-300 rounded-none"
                            />
                            <span className={`text-xs ${isActive ? "font-medium text-gray-800" : "font-medium text-gray-600"}`}>
                              {section.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* --- RIGHT SIDE: Action Buttons --- */}
        {IsQuerySelected && (<div className="action-buttons-secondary flex items-center flex-nowrap gap-1.5 shrink-0">
          {(isFromSingleVesselCalcy || isContractPage || isFromMatrix) && (
            <button className={btnClass} onClick={onSetOpenMap}>
              <BiSolidMapPin size={iconSize} className="calc-icon-color" />
              <span>Open Map</span>
            </button>
          )}

          {isFromSingleVesselCalcy && onClickCreateContract && (
            <button className={btnClass} onClick={onClickCreateContract}>
              <LiaFileContractSolid size={iconSize} className="calc-icon-color" />
              <span>Create Contract</span>
            </button>
          )}

          <button className={btnClass} onClick={onClickCalculate} data-spotlight="matrix-calculate-btn">
            <BiCalculator size={iconSize} className="calc-icon-color" />
            Calculate
          </button>

          {isContractPage && isPublic && (
            <button className={btnClass} onClick={onClickCopyToEstimate}>
              <MdSave size={iconSize} className="calc-icon-color" />
              To Estimation
            </button>
          )}

          {!isFromSavedContracts && isOperationPage && isPublic && (
            <button className={btnClass} onClick={onClickCopyToEstimate}>
              <MdSave size={iconSize} className="calc-icon-color" />
              Assign Self
            </button>
          )}

          {!isPublic && (
            <button className={btnClass} onClick={onClickCalculateAndSave}>
              <MdSave size={iconSize} className="calc-icon-color" />
              <span>Calculate & Save</span>
            </button>
          )}

          {(!isFromSingleVesselCalcy && (isFromCargoAnalysis || isFromMatrix)) && (
            <div className={btnClass + " cursor-pointer"} onClick={handleOpenPdfModal}>
              <GrDocumentPdf size={12} className="calc-icon-color" style={{ color: "#fff" }} />
              <span>Download PDF</span>
            </div>
          )}
        </div>)}
      </div>

      <CommonPdfPreviewModal
        open={pdfModal}
        onClose={handleClosePdfModal}
        title="Voyage Calculation PDF Preview"
        subtitle="A4 Landscape Report • Smart Sea Freight"
        document={<PdfDataComponent data={pdfdata} />}
        downloadFileName={`Voyage_Calculation_${Date.now()}.pdf`}
      />
    </div >
  );
}

export default ActionButtons;